// canvas を通さない PNG の作成・読み込み (範囲の移動・コピー用、
// docs/_wip/region-move-2026-10-15.md「bit-perfect の注意」)。
//
// ブラウザの canvas (Canvas2D) は premultiplied alpha なので、半透明ピクセルを
// `putImageData` → `toBlob` / `createImageBitmap` → `getImageData` と通すと値が変わる。
// 範囲の移動は「同じ絵を置き直す」ので、そのずれが動かすたびに溜まる。そこで straight-alpha の
// RGBA をそのまま PNG にし、読むと同じ値に戻る純 JS の実装を使う (zlib のみ fflate)。
//
// 対応範囲:
//   - encode: 8bit RGBA (colour type 6)、非インターレース。行ごとに PNG フィルタを選ぶ。
//   - decode: 8bit の colour type 6 (RGBA) / 2 (RGB) / 0 (Gray) / 4 (Gray+Alpha)、
//     非インターレース。これは移動用に自分たちが作る PNG と、sharp が出す raw 相当の
//     PNG を読むためで、パレット・16bit・インターレースは対象外 (throw)。
//
// DOM / Node 依存なし (browser worker / main / server の drawing-core 共用)。
import { unzlibSync, zlibSync } from 'fflate';
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
/** decode が受け付ける最大ピクセル数 (メモリ爆弾の防御)。4096² 相当。 */
export const MAX_RGBA_PNG_PIXELS = 4096 * 4096;
let crcTable = null;
const getCrcTable = () => {
    if (crcTable !== null)
        return crcTable;
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++)
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    crcTable = t;
    return t;
};
const crc32 = (bytes, start, end) => {
    const t = getCrcTable();
    let c = 0xffffffff;
    for (let i = start; i < end; i++)
        c = (t[(c ^ (bytes[i] ?? 0)) & 0xff] ?? 0) ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
};
const writeU32 = (buf, off, v) => {
    buf[off] = (v >>> 24) & 0xff;
    buf[off + 1] = (v >>> 16) & 0xff;
    buf[off + 2] = (v >>> 8) & 0xff;
    buf[off + 3] = v & 0xff;
};
const readU32 = (buf, off) => (((buf[off] ?? 0) << 24) |
    ((buf[off + 1] ?? 0) << 16) |
    ((buf[off + 2] ?? 0) << 8) |
    (buf[off + 3] ?? 0)) >>>
    0;
const makeChunk = (type, data) => {
    const out = new Uint8Array(12 + data.length);
    writeU32(out, 0, data.length);
    for (let i = 0; i < 4; i++)
        out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    writeU32(out, 8 + data.length, crc32(out, 4, 8 + data.length));
    return out;
};
const paeth = (a, b, c) => {
    const p = a + b - c;
    const pa = Math.abs(p - a);
    const pb = Math.abs(p - b);
    const pc = Math.abs(p - c);
    if (pa <= pb && pa <= pc)
        return a;
    if (pb <= pc)
        return b;
    return c;
};
/**
 * straight-alpha の RGBA を PNG (colour type 6, 8bit) にする。値は一切変えない
 * (alpha=0 の RGB も含めて、読み戻すと完全に同じ)。
 * `rgba.length === width * height * 4` でなければ throw。
 */
export const encodeRgbaPng = (rgba, width, height) => {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
        throw new Error('encodeRgbaPng: invalid size');
    }
    if (rgba.length !== width * height * 4)
        throw new Error('encodeRgbaPng: length mismatch');
    const stride = width * 4;
    const raw = new Uint8Array((stride + 1) * height);
    const cand = [0, 1, 2, 3, 4].map(() => new Uint8Array(stride));
    for (let y = 0; y < height; y++) {
        const row = y * stride;
        const up = row - stride;
        // 5 種のフィルタを試し、絶対値和 (符号付きバイトとして) が最小のものを選ぶ (標準的な経験則)。
        let best = 0;
        let bestSum = Number.POSITIVE_INFINITY;
        for (let f = 0; f < 5; f++) {
            const out = cand[f];
            let sum = 0;
            for (let i = 0; i < stride; i++) {
                const x = rgba[row + i] ?? 0;
                const a = i >= 4 ? (rgba[row + i - 4] ?? 0) : 0;
                const b = y > 0 ? (rgba[up + i] ?? 0) : 0;
                const c = y > 0 && i >= 4 ? (rgba[up + i - 4] ?? 0) : 0;
                let v;
                switch (f) {
                    case 0:
                        v = x;
                        break;
                    case 1:
                        v = x - a;
                        break;
                    case 2:
                        v = x - b;
                        break;
                    case 3:
                        v = x - ((a + b) >> 1);
                        break;
                    default:
                        v = x - paeth(a, b, c);
                        break;
                }
                v &= 0xff;
                out[i] = v;
                sum += v < 128 ? v : 256 - v;
            }
            if (sum < bestSum) {
                bestSum = sum;
                best = f;
            }
        }
        raw[y * (stride + 1)] = best;
        raw.set(cand[best], y * (stride + 1) + 1);
    }
    const idat = zlibSync(raw, { level: 6 });
    const ihdr = new Uint8Array(13);
    writeU32(ihdr, 0, width);
    writeU32(ihdr, 4, height);
    ihdr[8] = 8; // bit depth
    ihdr[9] = 6; // RGBA
    ihdr[10] = 0; // deflate
    ihdr[11] = 0; // filter method 0
    ihdr[12] = 0; // no interlace
    const parts = [
        Uint8Array.from(PNG_SIGNATURE),
        makeChunk('IHDR', ihdr),
        makeChunk('IDAT', idat),
        makeChunk('IEND', new Uint8Array(0)),
    ];
    const total = parts.reduce((n, p) => n + p.length, 0);
    const png = new Uint8Array(total);
    let off = 0;
    for (const p of parts) {
        png.set(p, off);
        off += p.length;
    }
    return png;
};
/**
 * PNG を straight-alpha の RGBA に読む。値は変えない (premultiply しない)。
 * 対応外 / 壊れた PNG / `maxPixels` 超過は throw。RGB / Gray は alpha=255 で返す。
 */
export const decodeRgbaPng = (bytes, maxPixels = MAX_RGBA_PNG_PIXELS) => {
    if (bytes.length < 8 + 12 + 13)
        throw new Error('decodeRgbaPng: too short');
    for (let i = 0; i < 8; i++) {
        if (bytes[i] !== PNG_SIGNATURE[i])
            throw new Error('decodeRgbaPng: bad signature');
    }
    let off = 8;
    let width = 0;
    let height = 0;
    let colorType = -1;
    let sawHeader = false;
    const idatParts = [];
    let sawEnd = false;
    while (off + 12 <= bytes.length) {
        const len = readU32(bytes, off);
        const type = String.fromCharCode(bytes[off + 4] ?? 0, bytes[off + 5] ?? 0, bytes[off + 6] ?? 0, bytes[off + 7] ?? 0);
        const dataStart = off + 8;
        const dataEnd = dataStart + len;
        if (dataEnd + 4 > bytes.length)
            throw new Error('decodeRgbaPng: truncated chunk');
        if (type === 'IHDR') {
            if (len !== 13)
                throw new Error('decodeRgbaPng: bad IHDR');
            width = readU32(bytes, dataStart);
            height = readU32(bytes, dataStart + 4);
            const depth = bytes[dataStart + 8];
            colorType = bytes[dataStart + 9] ?? -1;
            const interlace = bytes[dataStart + 12];
            if (depth !== 8)
                throw new Error('decodeRgbaPng: unsupported bit depth');
            if (interlace !== 0)
                throw new Error('decodeRgbaPng: interlace unsupported');
            if (colorType !== 6 && colorType !== 2 && colorType !== 0 && colorType !== 4) {
                throw new Error('decodeRgbaPng: unsupported colour type');
            }
            if (width <= 0 || height <= 0 || width * height > maxPixels) {
                throw new Error('decodeRgbaPng: bad or too large size');
            }
            sawHeader = true;
        }
        else if (type === 'IDAT') {
            idatParts.push(bytes.subarray(dataStart, dataEnd));
        }
        else if (type === 'IEND') {
            sawEnd = true;
            break;
        }
        off = dataEnd + 4;
    }
    if (!sawHeader || idatParts.length === 0 || !sawEnd)
        throw new Error('decodeRgbaPng: incomplete');
    const bpp = colorType === 6 ? 4 : colorType === 2 ? 3 : colorType === 4 ? 2 : 1;
    const stride = width * bpp;
    const expected = (stride + 1) * height;
    let idat;
    if (idatParts.length === 1) {
        idat = idatParts[0];
    }
    else {
        idat = new Uint8Array(idatParts.reduce((n, p) => n + p.length, 0));
        let o = 0;
        for (const p of idatParts) {
            idat.set(p, o);
            o += p.length;
        }
    }
    const raw = unzlibSync(idat, { out: new Uint8Array(expected) });
    if (raw.length !== expected)
        throw new Error('decodeRgbaPng: size mismatch');
    // フィルタを戻す (行ごと in place)。
    const pix = new Uint8Array(stride * height);
    for (let y = 0; y < height; y++) {
        const f = raw[y * (stride + 1)] ?? 0;
        const src = y * (stride + 1) + 1;
        const dst = y * stride;
        const up = dst - stride;
        for (let i = 0; i < stride; i++) {
            const x = raw[src + i] ?? 0;
            const a = i >= bpp ? (pix[dst + i - bpp] ?? 0) : 0;
            const b = y > 0 ? (pix[up + i] ?? 0) : 0;
            const c = y > 0 && i >= bpp ? (pix[up + i - bpp] ?? 0) : 0;
            let v;
            switch (f) {
                case 0:
                    v = x;
                    break;
                case 1:
                    v = x + a;
                    break;
                case 2:
                    v = x + b;
                    break;
                case 3:
                    v = x + ((a + b) >> 1);
                    break;
                case 4:
                    v = x + paeth(a, b, c);
                    break;
                default:
                    throw new Error('decodeRgbaPng: bad filter type');
            }
            pix[dst + i] = v & 0xff;
        }
    }
    if (colorType === 6) {
        return { rgba: new Uint8ClampedArray(pix.buffer, pix.byteOffset, pix.length), width, height };
    }
    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let p = 0; p < width * height; p++) {
        const s = p * bpp;
        const d = p * 4;
        if (colorType === 2) {
            rgba[d] = pix[s] ?? 0;
            rgba[d + 1] = pix[s + 1] ?? 0;
            rgba[d + 2] = pix[s + 2] ?? 0;
            rgba[d + 3] = 255;
        }
        else if (colorType === 0) {
            const g = pix[s] ?? 0;
            rgba[d] = g;
            rgba[d + 1] = g;
            rgba[d + 2] = g;
            rgba[d + 3] = 255;
        }
        else {
            const g = pix[s] ?? 0;
            rgba[d] = g;
            rgba[d + 1] = g;
            rgba[d + 2] = g;
            rgba[d + 3] = pix[s + 1] ?? 255;
        }
    }
    return { rgba, width, height };
};
//# sourceMappingURL=rgbaPng.js.map